import { Utensils, Wine, Receipt, Check } from 'lucide-react';
import { TABLE_STATE_COLORS } from '@/theme/statusColors';
import { shortName, fmtElapsed } from '../shared';

const CHAIR = '#2b1d16';
const RING = '#2563EB';

// Tamaño de la mesa según forma y capacidad (px)
export const unitSize = (forma, cap = 4) => {
  if (forma === 'redonda') return { w: cap > 4 ? 92 : 74, h: cap > 4 ? 92 : 74 };
  if (forma === 'rectangular') return { w: 70 + Math.ceil(Math.max(cap - 2, 2) / 2) * 34, h: 70 };
  if (forma === 'sombrilla') return { w: 70, h: 92 };
  if (forma === 'carpa') return { w: 104, h: 92 };
  return { w: cap > 2 ? 74 : 60, h: cap > 2 ? 74 : 60 };
};

// SVG con elementos fijos y paths (el editor visual envuelve listas/condicionales en <span>, inválido dentro de SVG)
const box = (x, y, bw, bh) => `M${x + 3} ${y}h${bw - 6}q3 0 3 3v${bh - 6}q0 3 -3 3h${-(bw - 6)}q-3 0 -3 -3v${-(bh - 6)}q0 -3 3 -3z`;
const rotBox = (cx, cy, bw, bh, a) => {
  const pts = [[-bw / 2, -bh / 2], [bw / 2, -bh / 2], [bw / 2, bh / 2], [-bw / 2, bh / 2]]
    .map(([px, py]) => [cx + px * Math.cos(a) - py * Math.sin(a), cy + px * Math.sin(a) + py * Math.cos(a)]);
  return `M${pts.map(p => p.map(n => n.toFixed(1)).join(' ')).join('L')}Z`;
};

const chairsRect = (w, h, cap, rect) => {
  const out = [];
  const pad = 13;
  if (!rect) {
    (cap <= 2 ? ['t', 'b'] : ['t', 'b', 'l', 'r']).forEach(s => out.push(s));
  } else {
    const ends = cap >= 6 ? 2 : 0;
    const top = Math.ceil((cap - ends) / 2);
    const bot = cap - ends - top;
    for (let i = 0; i < top; i++) out.push(['t', (i + 1) / (top + 1)]);
    for (let i = 0; i < bot; i++) out.push(['b', (i + 1) / (bot + 1)]);
    if (ends) out.push('l', 'r');
  }
  return out.map((c) => {
    const [side, f] = Array.isArray(c) ? c : [c, 0.5];
    const x = pad + (w - pad * 2) * f;
    const y = pad + (h - pad * 2) * f;
    if (side === 't') return box(x - 11, 1, 22, 11);
    if (side === 'b') return box(x - 11, h - 12, 22, 11);
    if (side === 'l') return box(1, y - 11, 11, 22);
    return box(w - 12, y - 11, 11, 22);
  }).join(' ');
};

const chairsRound = (w, cap) => {
  const n = Math.max(cap, 2);
  const r = w / 2 - 6;
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return rotBox(w / 2 + Math.cos(a) * r, w / 2 + Math.sin(a) * r, 20, 12, a + Math.PI / 2);
  }).join(' ');
};

const chairsPath = (forma, w, h, cap) => {
  if (forma === 'sombrilla') return `${box(6, 66, 24, 24)} ${box(40, 66, 24, 24)}`;
  if (forma === 'carpa') return '';
  return forma === 'redonda' ? chairsRound(w, cap) : chairsRect(w, h, cap, forma === 'rectangular');
};

const octagon = (w, r) => Array.from({ length: 8 }, (_, i) => {
  const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
  return `${(w / 2 + Math.cos(a) * r).toFixed(1)} ${(34 + Math.sin(a) * r).toFixed(1)}`;
}).join('L');

const bodyPath = (forma, w, h, simple) => {
  if (forma === 'redonda') {
    const r = w / 2 - (simple ? 3 : 15);
    return `M${w / 2 - r} ${h / 2}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
  }
  if (forma === 'sombrilla') return `M${octagon(w, 30)}Z`;
  if (forma === 'carpa') return box(4, 4, w - 8, h - 8);
  const inset = simple ? 3 : 13;
  return box(inset, inset, w - inset * 2, h - inset * 2);
};

const detailPath = (forma, w, h) => {
  if (forma === 'sombrilla') return Array.from({ length: 8 }, (_, i) => { const a = (i / 8) * Math.PI * 2; return `M${w / 2} 34L${(w / 2 + Math.cos(a) * 30).toFixed(1)} ${(34 + Math.sin(a) * 30).toFixed(1)}`; }).join(' ');
  if (forma === 'carpa') return `M4 4L${w / 2} ${h / 2}L${w - 4} 4M4 ${h - 4}L${w / 2} ${h / 2}L${w - 4} ${h - 4}`;
  return '';
};

const UnitSvg = ({ forma, w, h, cap, fill, stroke, dashed, selected, simple }) => (
  <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
    <path d={simple ? '' : chairsPath(forma, w, h, cap)} fill={forma === 'sombrilla' ? '#f4f1ea' : CHAIR} stroke={forma === 'sombrilla' ? '#9c8f78' : 'none'} />
    <path d={bodyPath(forma, w, h, simple)} fill={fill} stroke={selected ? RING : stroke || 'rgba(0,0,0,.18)'}
      strokeWidth={selected ? 4 : dashed ? 3 : 1.5} strokeDasharray={dashed && !selected ? '7 5' : undefined} />
    <path d={simple ? '' : detailPath(forma, w, h)} stroke="rgba(255,255,255,.35)" strokeWidth={1.5} fill="none" />
  </svg>
);

const Activity = ({ u }) => {
  const icons = [];
  if (u.session_id && u.state !== 'free') icons.push(Utensils);
  if (u.pending_orders) icons.push(Wine);
  if (u.state === 'bill_requested' || u.state === 'paying') icons.push(Receipt);
  if (u.ready_count) icons.push(Check);
  return icons.slice(0, 3).map((I, i) => <I key={i} className="w-3 h-3" aria-hidden="true" />);
};

export const unitLabel = (u, now) => {
  if (u.state === 'to_clean') return 'LIMPIAR';
  if (u.state === 'reserved' && u.reservation) return `RES ${u.reservation.time}`;
  if (u.session_id) return fmtElapsed(u.opened_at, now);
  return '';
};

export const FloorUnit = ({ unit: u, now, selected, simple, onClick, onDoubleClick, draggable, onDragStart }) => {
  const c = TABLE_STATE_COLORS[u.state] || TABLE_STATE_COLORS.free;
  const { w, h } = unitSize(u.forma, u.capacity);
  const label = unitLabel(u, now);
  const alerts = (u.call_since ? 1 : 0) + (u.ready_count || 0) + (u.pending_orders || 0);
  const textY = u.forma === 'sombrilla' ? 34 : h / 2;
  return (
    <button type="button" onClick={onClick} onDoubleClick={onDoubleClick} draggable={draggable} onDragStart={onDragStart}
      className={`mfloor-unit absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center ${draggable ? 'cursor-move' : ''} ${u.state === 'waiter_call' ? 'table-blink' : ''}`}
      style={{ left: `${u.position_x ?? 50}%`, top: `${u.position_y ?? 50}%` }}
      data-testid={`floor-unit-${u.unit_id}`} data-state={u.state} aria-label={`${u.name}: ${u.state}`}>
      <span className="relative block" style={{ width: w, height: h }}>
        <UnitSvg forma={u.forma} w={w} h={h} cap={u.capacity} fill={c.bg} stroke={c.border} dashed={!!c.border} selected={selected} simple={simple} />
        <span className="absolute inset-x-0 flex flex-col items-center leading-none pointer-events-none" style={{ top: textY - 15, color: c.fg }}>
          <span className="font-bold text-[19px]">{shortName(u.name)}</span>
          <span className="text-[11px] font-semibold mt-0.5">{u.capacity}p</span>
        </span>
        {alerts > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#DC2626] text-white text-[11px] font-bold grid place-items-center ring-2 ring-white"
            data-testid={`floor-alert-${u.unit_id}`}>{alerts}</span>
        )}
      </span>
      {label && (
        <span className="mt-1 inline-flex items-center gap-1 rounded-md bg-[#111827] text-white text-[11px] font-semibold px-1.5 py-0.5 shadow"
          data-testid={`floor-label-${u.unit_id}`}>
          {!simple && <Activity u={u} />}{label}
        </span>
      )}
    </button>
  );
};
